import axios from "axios";
import {AppDispatch, RootState} from "../../app/store";
import {IBarcode} from "../../interfaces/Barcode";
import {StaticTextModel, VariableTextModel} from "../../interfaces/LabelModel";
import {BARCODE, HORIZONTAL_LINE, PICTURE, SIMPLE_TEXT, VARIABLE_PICTURE, VARIABLE_TEXT, VERTICAL_LINE} from "../../utils/ItemTypes";
import {IItem} from "../../interfaces/Item";
import {ILabel} from "../../interfaces/Label";
import {saveAs} from "file-saver";
import {IPictureModel} from "../../interfaces/Picture";
import {ILineModel} from "../../interfaces/Line";
import {showErrorToast, showToast} from "../../utils/showToast";
import {SET_RECID} from "../MainReducer";
import { removeDateFromName } from "../../utils/constants";
import i18n from "i18next";

export const ExportXml = () => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const {labelItems, labelParams} = getState().labelEdit.present;
      const {templateName} = getState().refData;
      const label = parseLabelObject(labelItems, labelParams);

      const response = await axios.post(`api/Export/GenerateXml`, label, {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob",
      });

      if (response.status === 200) {
        const blob = new Blob([response.data], {type: "application/xml"});
        let fileName = templateName === null || templateName === undefined || templateName === "" ? `template-${Math.round(Math.random() * 10000)}.xml` : `${templateName}.xml`;
        fileName = removeDateFromName(fileName);
        saveAs(blob, fileName);
      }
    } catch (error) {
      showErrorToast(i18n.t("export_xml_error"), i18n.t("error") + ". " + error);
    }
  };
};

export const ExportPng = () => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const {labelItems, labelParams} = getState().labelEdit.present;
      const label = parseLabelObject(labelItems, labelParams);

      const response = await axios.post(`api/Export/GeneratePng`, label, {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob",
      });

      if (response.status === 200) {
        const blob = new Blob([response.data], {type: "image/png"});
        return blob;
      }
    } catch (error) {
      showErrorToast(i18n.t("export_png_error"), i18n.t("error") + ". " + error);
      return null;
    }
    return null;
  };
};


export const ReprintTemplate = (id: number) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const response = await axios.post(`api/Export/Reprint/${id}`, null, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200 && response.data?.success) {
        const { fileContent, width, height, dpi } = response.data;

        if (!fileContent) {
          showErrorToast(i18n.t("error"), i18n.t("template_not_found"));
          return null;
        }
        const byteString = atob(fileContent);
        const byteArray = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
          byteArray[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: "image/png" });

        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.src = url;

        img.onload = () => {
          let printWindow = window.open("", "_blank");
          if (printWindow) {
            const cleanupUrl = () => {
              try {
                URL.revokeObjectURL(url);
              } catch {
                // ignore
              }
            };

            printWindow.document.write(`
              <html style="height: 100%;">
                <head>
                  <meta name="viewport" content="width=device-width, minimum-scale=0.1">
                  <title>label</title>
                </head>
                <body style="margin: 0px; height: 100%; background-color: rgb(14, 14, 14);">
                  <img style="width: ${width}mm; height: ${height}mm;" src="${url}">
                </body>
              </html>
            `);
            printWindow.document.close();

            try {
              (printWindow as any).onafterprint = () => {
                cleanupUrl();
                try {
                  printWindow?.close();
                } catch {
                  // ignore
                }
              };
            } catch {
              // ignore
            }

            printWindow.onload = function () {
              printWindow!.print();
              setTimeout(() => {
                try {
                  printWindow!.close();
                } catch {
                  // ignore
                }
              }, 0);
            };

            setTimeout(() => cleanupUrl(), 60_000);
          } else {
            try {
              URL.revokeObjectURL(url);
            } catch {
              // ignore
            }
          }
        };

        img.onerror = () => {
          try {
            URL.revokeObjectURL(url);
          } catch {
            // ignore
          }
          showErrorToast(i18n.t("error"), i18n.t("template_not_found"));
        };

        return blob;
      } else {
        showErrorToast(i18n.t("error"), response.data?.msg || "Unknown error");
      }
    } catch (error) {
      showErrorToast(i18n.t("error"), i18n.t("template_not_found"));
      return null;
    }
    return null;
  };
};


export const ExportServer = (company: number = -1, isNewName: boolean = false, newName: string = "", overrideRecid?: number) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      let companyId = getState().auth.companyId;
      let recid = overrideRecid ? overrideRecid : getState().refData.recid;
      let templateName = isNewName ? newName : getState().refData.templateName;
      if (company !== null && company !== undefined && company !== -1) {
        companyId = company;
      }
      if (companyId === null || companyId === undefined || companyId === -1) {
        showErrorToast(i18n.t("export_server_error"), i18n.t("company_not_found"));
        return;
      }
      const {labelItems, labelParams} = getState().labelEdit.present;
      const label = parseLabelObject(labelItems, labelParams);

      const response = await axios.post(`api/Export/ServerExport/${companyId}/${recid}/${isNewName}/${templateName}`, label, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data.success) {
        dispatch({type: SET_RECID, recid: response.data.recid, templateName: response.data.templateName});
        showToast("success", "check circle outline", i18n.t("export_server_success"), i18n.t(response.data.msg));
      } else {
        showErrorToast(i18n.t("export_server_error"), i18n.t(response.data.msg));
      }
    } catch (error: any) {
      if(error.response && error.response.status !== 403){
        showErrorToast(i18n.t("export_server_error"), i18n.t("error") + ". " + error);
      }
    }
  };
};

export const parseLabelObject = (labelItems: IItem[], labelParams: ILabel) => {
  let barPictureList: IBarcode[] = [];
  let staticTextList: StaticTextModel[] = [];
  let variableTextList: VariableTextModel[] = [];
  let variablePictureList: IPictureModel[] = [];
  let staticPictureList: IPictureModel[] = [];
  let lineList: ILineModel[] = [];
  labelItems.forEach((item: IItem) => {
    switch (item.type) {
      case BARCODE:
        const barcode: IBarcode = {
          X: Number(item.xInMM),
          Y: Number(item.yInMM),
          Name: item.name || "",
          Width: Number(item.width) || 0,
          Height: Number(item.height) || 0,
          ShowCaption: item.showCaption ? 1 : 0,
          CaptionSize: Number(item.captionSize) || 10,
          Rotation: Number(item.rotation),
          Type: item.barcodeType || "C128",
          DefaultText: item.defaultText || "",
          DataMatrixSize: item.dataMatrixSize || "",
          bringBack: item.bringBack === 1 ? true : null,
        };
        barPictureList.push(barcode);
        break;
      case SIMPLE_TEXT:
        const text: StaticTextModel = {
          Caption: item.value || "",
          X: Number(item.xInMM),
          Y: Number(item.yInMM),
          Rotation: Number(item.rotation),
          FontSize: Number(item.fontSize) || 10,
          Bold: item.fontBold ? 1 : 0,
          Family: item.fontName || "Arial",
          bringBack: item.bringBack === 1 ? true : null,
        };
        staticTextList.push(text);
        break;
      case VARIABLE_TEXT:
        const variableText: VariableTextModel = {
          X: Number(item.xInMM),
          Y: Number(item.yInMM),
          Rotation: Number(item.rotation),
          FontSize: Number(item.fontSize) || 10,
          Bold: item.fontBold ? 1 : 0,
          Family: item.fontName || "Arial",
          Name: item.name || "",
          Align: item.align || "Left",
          Wrap: item.wrap ? 1 : 0,
          DefaultText: item.defaultText || "",
          Width: Number(item.width) || 0,
          bringBack: item.bringBack === 1 ? true : null,
        };
        variableTextList.push(variableText);
        break;
      case PICTURE:
        const staticPicture: IPictureModel = {
          X: Number(item.xInMM),
          Y: Number(item.yInMM),
          Width: Number(item.width) || 0,
          Height: Number(item.height) || 0,
          Path: item.path || "",
          base64: item.base64 || "",
          bringBack: item.bringBack === 1 ? true : null,
        };
        staticPictureList.push(staticPicture);
        break;
      case VARIABLE_PICTURE:
        const variablePicture: IPictureModel = {
          X: Number(item.xInMM),
          Y: Number(item.yInMM),
          Width: Number(item.width) || 0,
          Height: Number(item.height) || 0,
          Default: item.path || "",
          base64: item.base64 || "",
          Name: item.name || "",
          bringBack: item.bringBack === 1 ? true : null,
        };
        variablePictureList.push(variablePicture);
        break;
      case VERTICAL_LINE:
      case HORIZONTAL_LINE:
        const line: ILineModel = {
          X_from: Number(item.xInMM),
          Y_from: Number(item.yInMM),
          X_to: Number(item.xToInMM) || Number(item.xInMM),
          Y_to: Number(item.yToInMM) || Number(item.yInMM),
          Width: Number(item.lineWidth) || 0,
          bringBack: item.bringBack === 1 ? true : null,
        };
        lineList.push(line);
        break;
    }
  });

  const labelObject = {
    LabelType: "LabelLight",
    DaxLabel: {
      Width: Math.floor(labelParams.widthInPx / labelParams.pxInMM),
      Height: Math.floor(labelParams.heightInPx / labelParams.pxInMM),
      Orientation: labelParams.orientation,
      Dpi: labelParams.dpi,
      ShowDefaultValues: labelParams.showDefaultValues ? "True" : "False",
      GridCellWidth: labelParams.gridCellWidth && labelParams.gridCellWidth > 0 ? labelParams.gridCellWidth : null,
    },
    BarPictureList: barPictureList.length > 0 ? barPictureList : null,
    StaticTextList: staticTextList.length > 0 ? staticTextList : null,
    StaticPictureList: staticPictureList.length > 0 ? staticPictureList : null,
    VariablePictureList: variablePictureList.length > 0 ? variablePictureList : null,
    LineList: lineList.length > 0 ? lineList : null,
    VariableTextList: variableTextList.length > 0 ? variableTextList : null,
  };
  return labelObject;
};
